import { inject, Injectable, signal } from '@angular/core';
import { LoadingService } from '@mds/ngx-core';
import {
  defer,
  MonoTypeOperatorFunction,
  Observable,
  of,
  OperatorFunction,
  tap,
  withLatestFrom,
} from 'rxjs';
import { finalize } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class RplLoadingService {
  private readonly loadingService = inject(LoadingService);
  private readonly currentLoadingIds = signal<string[]>([]);

  readonly isLoading$ = this.loadingService.isLoading$;

  start(): string {
    const id = crypto.randomUUID();
    this.currentLoadingIds.update((currentIds) => [...currentIds, id]);

    if (this.currentLoadingIds().length <= 1) {
      this.loadingService.start();
    }
    return id;
  }

  end(id: string | undefined): void {
    if (!id) return;

    this.currentLoadingIds.update((currentIds) => {
      const newIds = currentIds.filter((currentId) => currentId !== id);
      return [...newIds];
    });

    if (this.currentLoadingIds().length <= 0) {
      this.loadingService.end();
    }
  }

  startLoading<T>(): OperatorFunction<T, [T, string]> {
    return withLatestFrom(of(this.start()));
  }

  endLoading<T>(): MonoTypeOperatorFunction<[T, string]> {
    return (source: Observable<[T, string]>) =>
      defer(() => {
        let loadingId: string;
        return source.pipe(
          tap(([_x, id]) => (loadingId = id)),
          finalize(() => this.end(loadingId)),
        );
      });
  }
}
